import { z } from 'zod'

// Canonical trigger values aligned with the backend (CK-2016). Legacy values
// `when_pod_uploaded` (migrated) and `when_pod_confirmed` (never accepted) must
// be normalized to `when_pod_received` on read and are never sent to the API.
export const PaymentTermTriggerSchema = z.enum([
  'prepayment',
  'prepayment_before_collection',
  'when_transport_finished',
  'when_pod_received',
  'selfbill',
  'consolidated_invoice',
])

export const PaymentTermsFormTriggerSchema = PaymentTermTriggerSchema.or(
  z.literal('company_default'),
)

export const PaymentTermPeriodSchema = z.enum([
  'OFCURRENTMONTH',
  'OFFOLLOWINGMONTH',
  'DAYSAFTERBILLDATE',
  'DAYSAFTERBILLMONTH',
])

export type PaymentTermsFormTrigger = z.infer<typeof PaymentTermsFormTriggerSchema>
export type PaymentTermTrigger = z.infer<typeof PaymentTermTriggerSchema>

export const PaymentTermsFormValueSchema = z.object({
  bill_date: z.date().nullish(),
  days: z.number().int().min(1).max(365).nullish(),
  period: PaymentTermPeriodSchema.nullish(),
  trigger: PaymentTermsFormTriggerSchema,
})

export type PaymentTermsFormValue = z.infer<typeof PaymentTermsFormValueSchema>

export const PaymentTermsResponseSchema = z.object({
  bill_date: z.string().nullish(),
  days: z.number().nullish(),
  is_inherited: z.boolean().nullish(),
  period: PaymentTermPeriodSchema.nullish(),
  title: z.string().nullish(),
  trigger: z.string().nullish(),
})

export const PaymentTermsPayloadSchema = z.object({
  bill_date: z.string().nullish(),
  days: z.number().nullish(),
  period: PaymentTermPeriodSchema.nullish(),
  trigger: PaymentTermTriggerSchema,
})

export type PaymentTermsPayload = z.infer<typeof PaymentTermsPayloadSchema>
export type PaymentTermsResponse = z.infer<typeof PaymentTermsResponseSchema>

// Read-side schema for company-level payment terms (customer `payment_terms`
// and account `carrier_payment_terms`). The trigger stays a loose string so
// legacy backend values never break parsing; helpers normalize them to the
// canonical `PaymentTermTriggerSchema` values for display and writing.
export const GetCompanyPaymentTermsSchema = z.object({
  days: z.number().nullish(),
  period: PaymentTermPeriodSchema.nullish(),
  title: z.string().nullish(),
  trigger: z.string().nullish(),
})

export type GetCompanyPaymentTerms = z.infer<typeof GetCompanyPaymentTermsSchema>

// Exact flat-key payload accepted by PATCH /companies/{id}/account for the
// carrier payment terms columns. Each key may be explicitly null to clear the
// column ("Not configured"). Never contains customer `payment_terms`,
// `bill_date`, or `title`.
export const CarrierPaymentTermsAccountPayloadSchema = z.object({
  carrier_payment_days: z.number().int().min(1).max(365).nullable(),
  carrier_payment_period: PaymentTermPeriodSchema.nullable(),
  carrier_payment_trigger: PaymentTermTriggerSchema.nullable(),
})

export type CarrierPaymentTermsAccountPayload = z.infer<
  typeof CarrierPaymentTermsAccountPayloadSchema
>
